import React, { ChangeEvent, ReactElement, useState } from 'react';

import { AnimatePresence } from 'framer-motion';
import { FieldValues } from 'react-hook-form';
import { FieldErrors } from 'react-hook-form/dist/types/errors';
import {
  UseFormRegister,
  UseFormSetValue,
} from 'react-hook-form/dist/types/form';
import styled, { css } from 'styled-components';

import { Button, IconButton } from 'chime-ui/components/Button';
import {
  CardVendorIcon,
  iconsPath,
  QuestionCircleIcon,
} from 'chime-ui/components/Icons';
import InlineErrorMessage from 'chime-ui/components/TextField/InlineErrorMessage';
import TextField from 'chime-ui/components/TextField/TextField';
import { colors } from 'chime-ui/system/colors';

import { handleExpdateEvents } from 'components/SubPages/ClaimInfo/utils/eventHandlers';
import {
  CardType,
  getCardVendor,
} from 'components/SubPages/ClaimInfo/utils/getCardVendor';
import {
  normalizeCardNumber,
  normalizeCSC,
  normalizePostalCode,
} from 'components/SubPages/ClaimInfo/utils/normalizers';

import { InputFieldTrackingFunction } from 'utils/segment/trackingEvents';

type CardFormProps = {
  register: UseFormRegister<FieldValues>;
  setValue: UseFormSetValue<FieldValues>;
  errors: FieldErrors<FieldValues>;
  isSubmitted: boolean;
  trackCVV: InputFieldTrackingFunction;
  trackExpDate: InputFieldTrackingFunction;
  trackCardNumber: InputFieldTrackingFunction;
  trackZipCode: InputFieldTrackingFunction;
  showBottomSheet: () => void;
  handleOnOcrClick?: () => void;
  isCameraButtonShowing?: boolean;
  setCameraButtonShowing?: (cardNumber: string) => void;
  publicId?: string;
};

function CardForm({
  register,
  setValue,
  errors,
  isSubmitted,
  trackCVV,
  trackExpDate,
  trackCardNumber,
  trackZipCode,
  showBottomSheet,
  handleOnOcrClick,
  isCameraButtonShowing,
  setCameraButtonShowing,
  publicId,
}: CardFormProps): ReactElement {
  const [cardVendor, setCardVendor] = useState<CardType>(null);
  const [isCardNumberFocused, setCardNumberFocused] = useState(false);

  const onCardNumberFocus = () => setCardNumberFocused(true);
  const onCardNumberBlur = () => setCardNumberFocused(false);

  const handleCardVendor = ({
    target: { value },
  }: ChangeEvent<HTMLInputElement>) => {
    const normalizedCardNumber = normalizeCardNumber(value);
    setValue('cardnumber', normalizedCardNumber);
    setCardVendor(getCardVendor(normalizedCardNumber));

    if (setCameraButtonShowing) {
      setCameraButtonShowing(value);
    }
  };

  const onOcrClick = () => {
    if (handleOnOcrClick) {
      handleOnOcrClick();
    }
  };

  return (
    <>
      <CCRow>
        <TextField
          {...register('cardnumber', {
            onBlur: e => {
              onCardNumberBlur();
              return trackCardNumber(e, publicId);
            },
            onChange: handleCardVendor,
          })}
          id={'cardnumber'}
          label={'Debit card number'}
          autoComplete="cc-number"
          type="tel"
          inputMode="numeric"
          hasError={!!errors.cardnumber && isSubmitted}
          onAutoFill={e => trackCardNumber(e, publicId)}
          onFocus={onCardNumberFocus}
          endAdornment={
            <>
              {cardVendor && (
                <CardVendorIcon
                  icon={cardVendor.type}
                  vendor={cardVendor.displayName}
                />
              )}
              {isCameraButtonShowing && (
                <CameraButton
                  onClick={onOcrClick}
                  isCardNumberFocused={isCardNumberFocused}
                />
              )}
            </>
          }
        />
      </CCRow>

      <AnimatePresence>
        {!!errors.cardnumber && isSubmitted && (
          <InlineErrorMessage key={'cardnumber'}>
            {errors.cardnumber?.message}
          </InlineErrorMessage>
        )}
      </AnimatePresence>

      <CardDetailsRow>
        <TextField
          {...register('expdate')}
          id={'expdate'}
          label={'MM/YY'}
          maxLength={5}
          autoComplete="cc-exp"
          type="tel"
          inputMode="numeric"
          hasError={!!errors.expdate && isSubmitted}
          onBlur={e => trackExpDate(e, publicId)}
          onAutoFill={e => trackExpDate(e, publicId)}
          onKeyUp={handleExpdateEvents.onKeyUp}
        />

        <TextField
          {...register('csc', {
            onChange: event => {
              const { value } = event.target;
              event.target.value = normalizeCSC(value);
            },
          })}
          id={'csc'}
          label={'CVV'}
          autoComplete="cc-csc"
          type="password"
          inputMode="numeric"
          hasError={!!errors.csc && isSubmitted}
          onAutoFill={e => trackCVV(e, publicId)}
          onBlur={e => trackCVV(e, publicId)}
          endAdornment={
            <QuestionIconLayout>
              <IconButton
                icon={<QuestionCircleIcon />}
                onClick={showBottomSheet}
                aria-label={'question-icon'}
              />
            </QuestionIconLayout>
          }
        />

        <TextField
          {...register('postalCode', {
            onChange: event => {
              const { value } = event.target;
              event.target.value = normalizePostalCode(value);
            },
          })}
          id={'postalCode'}
          label={'ZIP Code'}
          autoComplete="postal-code"
          type="tel"
          inputMode="numeric"
          hasError={!!errors.postalCode && isSubmitted}
          onAutoFill={e => trackZipCode(e, publicId)}
          onBlur={e => trackZipCode(e, publicId)}
        />
      </CardDetailsRow>
      <AnimatePresence>
        {!!errors.expdate && isSubmitted && (
          <InlineErrorMessage key={'expdate'}>
            {errors.expdate?.message}
          </InlineErrorMessage>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {!!errors.csc && isSubmitted && (
          <InlineErrorMessage key={'csc'}>
            {errors.csc?.message}
          </InlineErrorMessage>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {!!errors.postalCode && isSubmitted && (
          <InlineErrorMessage key={'postalCode'}>
            {errors.postalCode?.message}
          </InlineErrorMessage>
        )}
      </AnimatePresence>
    </>
  );
}

const CCRow = styled.div`
  padding-top: 0.5rem;
`;

const CardDetailsRow = styled.div`
  display: flex;
  flex-flow: row wrap;
  column-gap: 0.5rem;
  padding-top: 0.5rem;
`;

const QuestionIconLayout = styled.div`
  align-items: flex-start;
  padding: 0.5rem;
`;

const CameraButton = styled(Button).attrs({
  'aria-label': 'camera',
})`
  width: 5rem;
  border: none;
  border-radius: 0;
  height: 100%;
  transition: all 0.3s ease 0s;

  ${props =>
    props.isCardNumberFocused
      ? css`
          background: ${colors.brightGreen}
            url('${iconsPath}cameraLargeBlack.svg') no-repeat center center;
        `
      : css`
          background: url('${iconsPath}cameraLargeGreen.svg') no-repeat center
            center;
        `};
`;

export default CardForm;
